"use client"

import {
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
} from "@heroicons/react/16/solid"
import { ArrowUturnLeftIcon } from "@heroicons/react/20/solid"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { useMachineStageProgress } from "@src/components/DefuseSDK/features/common/useMachineStageProgress"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import { ProgressSteps } from "@src/components/ProgressIndicator"
import {
  getStageFromState,
  isWithdrawStatusError,
  isWithdrawStatusSuccess,
  WITHDRAW_STAGE_LABELS,
  WITHDRAW_STAGE_LABELS_SHORT,
  WITHDRAW_STAGES,
  type WithdrawStage,
} from "@src/features/withdraw/utils/withdrawStatusUtils"
import type { TrackedWithdrawIntent } from "@src/providers/WithdrawTrackerMachineProvider"
import { midTruncate } from "@src/shared/utils/display"
import Button from "./Button"
import { buildIntentsExplorerTxUrl } from "./DefuseSDK/constants/blockchains"

type WithdrawStatusProps = {
  withdraw: TrackedWithdrawIntent
  variant: "full" | "dock"
  onWithdrawAgain?: () => void
}

export function WithdrawStatus({
  withdraw,
  variant,
  onWithdrawAgain,
}: WithdrawStatusProps) {
  const { displayStage, displayIndex, contextStatus, stateValue } =
    useMachineStageProgress({
      actorRef: withdraw.actorRef,
      stages: WITHDRAW_STAGES,
      getStageFromState,
    })

  const isError = isWithdrawStatusError(contextStatus, stateValue)
  const isSuccess = isWithdrawStatusSuccess(contextStatus, stateValue)

  if (variant === "dock") {
    return (
      <ProgressSteps
        stages={WITHDRAW_STAGES}
        stageLabels={WITHDRAW_STAGE_LABELS_SHORT}
        displayStage={displayStage}
        displayIndex={displayIndex}
        isError={isError}
        isSuccess={isSuccess}
        size="sm"
      />
    )
  }

  return (
    <FullView
      withdraw={withdraw}
      displayStage={displayStage}
      displayIndex={displayIndex}
      isError={isError}
      isSuccess={isSuccess}
      onWithdrawAgain={onWithdrawAgain}
    />
  )
}

function FullView({
  withdraw,
  displayStage,
  displayIndex,
  isError,
  isSuccess,
  onWithdrawAgain,
}: {
  withdraw: TrackedWithdrawIntent
  displayStage: WithdrawStage
  displayIndex: number
  isError: boolean
  isSuccess: boolean
  onWithdrawAgain?: () => void
}) {
  const {
    tokenOut,
    amountOut,
    depositAddress,
    recipient,
    displayOverrides,
    recipientContactName,
  } = withdraw

  const formattedAmountOut = formatTokenValue(
    amountOut.amount,
    amountOut.decimals,
    { min: 0.0001, fractionDigits: 4 }
  )

  const explorerUrl = depositAddress
    ? buildIntentsExplorerTxUrl(depositAddress, {
        isConfidential: withdraw.isConfidential,
      })
    : null
  const successActionHref = displayOverrides?.successActionHref

  return (
    <>
      <div className="mt-7 rounded-3xl border border-gray-200 bg-white p-5 sm:p-6 dark:border-white/5 dark:bg-gray-800">
        <div className="flex flex-col items-center text-center">
          <AssetComboIcon icon={tokenOut?.icon} sizeClassName="size-13" />

          <div className="mt-5 text-balance font-bold text-2xl/7 text-gray-900 tracking-tight dark:text-white">
            Sending{" "}
            <span className="whitespace-nowrap">
              {formattedAmountOut} {tokenOut.symbol}
            </span>
            <br />
            to{" "}
            <span className="whitespace-nowrap">
              {displayOverrides?.recipientLabel ?? midTruncate(recipient)}
            </span>
          </div>

          {recipientContactName && (
            <div className="mt-2 flex items-center gap-1">
              <CheckBadgeIcon className="size-4 shrink-0 text-green-600" />
              <span className="font-medium text-base/5 text-gray-500 dark:text-gray-400">
                {recipientContactName}
              </span>
            </div>
          )}
        </div>

        <div className="mt-6 border-gray-200 border-t pt-6 sm:mt-8 sm:pt-8 dark:border-white/10">
          <ProgressSteps
            stages={WITHDRAW_STAGES}
            stageLabels={WITHDRAW_STAGE_LABELS}
            displayStage={displayStage}
            displayIndex={displayIndex}
            isError={isError}
            isSuccess={isSuccess}
            size="md"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {explorerUrl && (
          <Button
            href={explorerUrl}
            variant="secondary"
            size="xl"
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            View on explorer
            <ArrowTopRightOnSquareIcon className="size-4" />
          </Button>
        )}
        {isSuccess && (
          <Button
            size="xl"
            fullWidth
            href={successActionHref}
            onClick={successActionHref ? undefined : onWithdrawAgain}
          >
            {!successActionHref && (
              <ArrowUturnLeftIcon className="size-4.5 shrink-0" />
            )}
            {displayOverrides?.successActionLabel ?? "Send again"}
          </Button>
        )}
        {!isSuccess && !isError && (
          <Button
            size="xl"
            variant="secondary"
            fullWidth
            onClick={onWithdrawAgain}
          >
            Create a new send
          </Button>
        )}
        {isError && (
          <Button
            size="xl"
            variant="secondary"
            fullWidth
            onClick={onWithdrawAgain}
          >
            Try a new send
          </Button>
        )}
      </div>
    </>
  )
}
